/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid2";
import FormHelperText from "@mui/material/FormHelperText";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

import { updateCriticalIllness } from "../../../states/slcObjectives.js";

import {
  valudateTextfield2,
  validateCurrencyField2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { pvAnnuity } from "../../../utils/formula.js";

export default function CriticalIllness() {
  const criticalIlness = useSelector((state) => state.objectives.protection.criticalIlness);

  const [criMonthlyAmt, setCriMonthlyAmt] = useState(criticalIlness.criMonthlyAmt);

  const [criAnnualAmt, setCriAnnualAmt] = useState(criticalIlness.criAnnualAmt);
  const [criYearsRequired, setCriYearsRequired] = useState(criticalIlness.criYearsRequired);
  const [criPctReturnRate, setCriPctReturnRate] = useState(criticalIlness.criPctReturnRate);
  const [criPctInfRate, setCriPctInfRate] = useState(criticalIlness.criPctInfRate);
  const [criPctInfAdjRate, setCriPctInfAdjRate] = useState(criticalIlness.criPctInfAdjRate);
  const [criFundsRequired, setCriFundsRequired] = useState(criticalIlness.criFundsRequired);

  const [criLumpSum, setLumpSum] = useState(criticalIlness.criLumpSum);
  const [criOtherDesc, setCriOtherDesc] = useState(criticalIlness.criOtherDesc);
  const [criOtherDescError, setCriOtherDescError] = useState();
  const [criOtherAmt, setCriOtherAmt] = useState(criticalIlness.criOtherAmt);
  const [criTotalNeeds, setTotalNeeds] = useState(criticalIlness.criTotalNeeds);

  const [resLifeInsuranceCover, setResLifeInsuranceCover] = useState(criticalIlness.resLifeInsuranceCover);
  const [resCashAssets, setResCashAssets] = useState(criticalIlness.resCashAssets);
  const [resOthersDesc, setResOthersDesc] = useState(criticalIlness.resOthersDesc);
  const [resOthersDescError, setResOthersDescError] = useState();
  const [resOthersAmt, setResOthersAmt] = useState(criticalIlness.resOthersAmt);
  const [resTotalResources, setResTotalResources] = useState(criticalIlness.resTotalResources);

  const [criSurplusShortfall, setCriSurplusShortfall] = useState(criticalIlness.criSurplusShortfall);
  const [deviationReason, setDeviationReason] = useState(criticalIlness.deviationReason);
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcFundsRequired, [criAnnualAmt, criYearsRequired, criPctInfAdjRate]);

  useEffect(calcTotalNeeds, [criFundsRequired, criLumpSum, criOtherAmt]);

  useEffect(calcTotalResources, [resLifeInsuranceCover, resCashAssets, resOthersAmt]);

  useEffect(calcSurplusOrShortfall, [criTotalNeeds, resTotalResources]);

  /**==============================
     * FUNCTIONS
     ==============================*/
  function calcFundsRequired() {
    //CALCULATE PV
    let presentValue = pvAnnuity(
      Number(removeCommaFromCurrency(criAnnualAmt)),
      Number(criPctInfAdjRate) / 100,
      Number(criYearsRequired),
      1
    ).toFixed(2);

    updateRedux("criFundsRequired", presentValue, updateCriticalIllness);
    setCriFundsRequired(presentValue);
  }

  function calcTotalNeeds() {
    let totalNeeds =
      Number(removeCommaFromCurrency(criFundsRequired)) +
      Number(removeCommaFromCurrency(criLumpSum)) +
      Number(removeCommaFromCurrency(criOtherAmt));

    totalNeeds = convertLocaleString(totalNeeds, "en-US", 2);

    updateRedux("criTotalNeeds", totalNeeds, updateCriticalIllness);
    setTotalNeeds(totalNeeds);
  }

  function calcTotalResources() {
    let totalResources =
      Number(removeCommaFromCurrency(resLifeInsuranceCover)) +
      Number(removeCommaFromCurrency(resCashAssets)) +
      Number(removeCommaFromCurrency(resOthersAmt));

    totalResources = convertLocaleString(totalResources, "en-US", 2);

    updateRedux("resTotalResources", totalResources, updateCriticalIllness);
    setResTotalResources(totalResources);
  }

  function calcSurplusOrShortfall() {
    let surplusShortfall =
      Number(removeCommaFromCurrency(resTotalResources)) - Number(removeCommaFromCurrency(criTotalNeeds));

    surplusShortfall = convertLocaleString(surplusShortfall, "en-US", 2);

    updateRedux("criSurplusShortfall", surplusShortfall, updateCriticalIllness);
    setCriSurplusShortfall(surplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    //CRITICAL ILLNESS - NEEDS
    if (id === "criMonthlyAmt") {
      input_data.fieldName = "Monthly income";
      input_data.fieldVariable = criMonthlyAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        //Annual Amount - Calculation
        let annualAmount = Number(removeCommaFromCurrency(result.value)) * 12;
        annualAmount = convertLocaleString(annualAmount, "en-US", 2);

        updateRedux("criMonthlyAmt", result.value, updateCriticalIllness);
        updateRedux("criAnnualAmt", annualAmount, updateCriticalIllness);
        setCriMonthlyAmt(result.value);
        setCriAnnualAmt(annualAmount);
      }
    }

    if (id === "criYearsRequired") {
      input_data.fieldName = "Years";
      input_data.fieldVariable = criYearsRequired;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setCriYearsRequired(result.value);
        updateRedux("criYearsRequired", result.value, updateCriticalIllness);
      }
    }

    if (id === "criPctReturnRate") {
      input_data.fieldName = "Rate of return (%)";
      input_data.fieldVariable = criPctReturnRate;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setCriPctReturnRate(result.value);
        updateRedux("criPctReturnRate", result.value, updateCriticalIllness);
      }
    }

    if (id === "criPctInfRate") {
      input_data.fieldName = "Assumed inflation rate (%)";
      input_data.fieldVariable = criPctInfRate;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setCriPctInfRate(result.value);
        updateRedux("criPctInfRate", result.value, updateCriticalIllness);
      }
    }

    if (id === "criPctInfAdjRate") {
      input_data.fieldName = "Inflation adjusted Rate";
      input_data.fieldVariable = criPctInfAdjRate;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setCriPctInfAdjRate(result.value);
        updateRedux("criPctInfAdjRate", result.value, updateCriticalIllness);
      }
    }

    if (id === "criLumpSum") {
      input_data.fieldName = "Treatment lump sum";
      input_data.fieldVariable = criLumpSum;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLumpSum(result.value);
        updateRedux("criLumpSum", result.value, updateCriticalIllness);
      }
    }

    if (id === "criOtherDesc") {
      input_data.fieldName = "Treatment lump sum";
      input_data.fieldVariable = criOtherDesc;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setCriOtherDesc(result.value);
        updateRedux("criOtherDesc", result.value, updateCriticalIllness);
      }

      setCriOtherDescError(result.errorMessage);
    }

    if (id === "criOtherAmt") {
      input_data.fieldName = "Other amount";
      input_data.fieldVariable = criOtherAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setCriOtherAmt(result.value);
        updateRedux("criOtherAmt", result.value, updateCriticalIllness);
      }
    }

    //CRITICAL ILLNESS - ALLOCATED RESOURCES
    if (id === "resLifeInsuranceCover") {
      input_data.fieldName = "Insurance cover";
      input_data.fieldVariable = resLifeInsuranceCover;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResLifeInsuranceCover(result.value);
        updateRedux("resLifeInsuranceCover", result.value, updateCriticalIllness);
      }
    }

    if (id === "resCashAssets") {
      input_data.fieldName = "Cash assets";
      input_data.fieldVariable = resCashAssets;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResCashAssets(result.value);
        updateRedux("resCashAssets", result.value, updateCriticalIllness);
      }
    }

    if (id === "resOthersDesc") {
      input_data.fieldName = "Other resource amount";
      input_data.fieldVariable = resOthersDesc;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setResOthersDesc(result.value);
        updateRedux("resOthersDesc", result.value, updateCriticalIllness);
      }

      setResOthersDescError(result.errorMessage);
    }

    if (id === "resOthersAmt") {
      input_data.fieldName = "Other amount";
      input_data.fieldVariable = resOthersAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResOthersAmt(result.value);
        updateRedux("resOthersAmt", result.value, updateCriticalIllness);
      }
    }

    //CRITICAL ILLNESS - SURPLUS AND SHORTFALL
    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updateCriticalIllness);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={1} size={12}>
        <GridSectionHeader
          sectionName=" In the Event of Critical Illness"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            backgroundColor: "#10B981",
          }}
        />

        {/*==============================
        INCOME
        ==============================*/}
        <Grid size={12}>
          <Typography variant="h5" sx={{ fontWeight: "bold", textDecoration: "underline" }}>
            Critical Illness Needs
          </Typography>
        </Grid>

        {/*==============================
        MONTHLY AMOUNT NEEDS
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Monthly amount needs for living expenses and/or dependants
          </Typography>
        </Grid>
        <GridTextfield
          id="criMonthlyAmt"
          key="criMonthlyAmt"
          columnSize="3"
          label="Monthly"
          isCurrency={true}
          currState={criMonthlyAmt}
          onChange={handleChange}
        />
      </Grid>

      {/*==============================
        ANNUAL AMOUNT NEEDS
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Annual Amount Needs for Living Expenses and/or Dependants (pmt)
        </Typography>
      </Grid>
      <GridTextfield
        id="criAnnualAmt"
        key="criAnnualAmt"
        columnSize="3"
        label="Annually"
        isCurrency={true}
        currState={criAnnualAmt}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        NUMBER OF YEARS REQUIRED
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Number of Years Required (n)
        </Typography>
      </Grid>
      <GridTextfield
        id="criYearsRequired"
        key="criYearsRequired"
        columnSize="3"
        label="Years"
        currState={criYearsRequired}
        onChange={handleChange}
      />

      {/*==============================
        INVESTMENT RATE OF RETURN
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 0.5,
          }}
        >
          Investment Rate of Return (%) (R)
        </Typography>
        <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
      </Grid>

      <GridTextfield
        id="criPctReturnRate"
        key="criPctReturnRate"
        columnSize="3"
        label="Percent"
        currState={criPctReturnRate}
        onChange={handleChange}
      />

      {/*==============================
        ASSUMED INFLATION RATE
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 0.5,
          }}
        >
          Assumed Inflation Rate. (%) (i)
        </Typography>
        <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
      </Grid>

      <GridTextfield
        id="criPctInfRate"
        key="criPctInfRate"
        columnSize="3"
        label="Percent"
        currState={criPctInfRate}
        onChange={handleChange}
      />

      {/*==============================
        INFLATION ADJUSTED RATE OF RETURN FROM INVESTMENT
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Inflation-adjusted Rate of Return from investments(%) (i)
        </Typography>
      </Grid>

      <GridTextfield
        id="criPctInfAdjRate"
        key="criPctInfAdjRate"
        columnSize="3"
        label="Percent"
        currState={criPctInfAdjRate}
        onChange={handleChange}
      />

      {/*==============================
        FUND REQUIRED TO PROVIDE INCOME
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Funds Required (pv)
        </Typography>
      </Grid>
      <GridTextfield
        id="criFundsRequired"
        key="criFundsRequired"
        columnSize="3"
        isCurrency={true}
        currState={criFundsRequired}
        onChange={handleChange}
      />

      {/*==============================
        LUMP SUM REQUIRED FOR TREATMENT OF ILLNESS
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Lump Sum Required for Treatment of Illness
        </Typography>
      </Grid>
      <GridTextfield
        id="criLumpSum"
        key="criLumpSum"
        columnSize="3"
        isCurrency={true}
        currState={criLumpSum}
        onChange={handleChange}
      />

      {/*==============================
        EXPENSES - OTHERS
        ==============================*/}
      <Grid size={1}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Others:
        </Typography>
      </Grid>
      <GridTextfield
        id="criOtherDesc"
        key="criOtherDesc"
        columnSize="8"
        currState={criOtherDesc}
        onChange={handleChange}
        error={criOtherDescError}
        helperText={criOtherDescError}
      />
      <GridTextfield
        id="criOtherAmt"
        key="criOtherAmt"
        columnSize="3"
        isCurrency={true}
        currState={criOtherAmt}
        onChange={handleChange}
      />

      {/*==============================
        TOTAL CRITICAL ILLNESS NEEDS
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          (A) Total Critical Illness Needs
        </Typography>
      </Grid>
      <GridTextfield
        id="criTotalNeeds"
        key="criTotalNeeds"
        columnSize="3"
        isCurrency={true}
        currState={criTotalNeeds}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        EXISTING RESOURCES ALLOCATED
      ==============================*/}
      <Grid size={8}>
        <Typography variant="h5" sx={{ fontWeight: "bold", textDecoration: "underline" }}>
          Existing Resources Allocated (Current Values)
        </Typography>
      </Grid>
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Life Insurance Coverage
        </Typography>
      </Grid>
      <GridTextfield
        id="resLifeInsuranceCover"
        key="resLifeInsuranceCover"
        columnSize="3"
        isCurrency={true}
        currState={resLifeInsuranceCover}
        onChange={handleChange}
      />

      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Cash Assets (Savings, Fixed Deposits, etc.)
        </Typography>
      </Grid>
      <GridTextfield
        id="resCashAssets"
        key="resCashAssets"
        columnSize="3"
        isCurrency={true}
        currState={resCashAssets}
        onChange={handleChange}
      />

      <Grid size={1}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Others:
        </Typography>
      </Grid>
      <GridTextfield
        id="resOthersDesc"
        key="resOthersDesc"
        columnSize="8"
        currState={resOthersDesc}
        onChange={handleChange}
      />
      <GridTextfield
        id="resOthersAmt"
        key="resOthersAmt"
        columnSize="3"
        isCurrency={true}
        currState={resOthersAmt}
        onChange={handleChange}
      />

      {/*==============================
        TOTAL
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (B) Total Resource Allocated
        </Typography>
      </Grid>
      <GridTextfield
        id="resTotalResources"
        key="resTotalResources"
        columnSize="3"
        isCurrency={true}
        currState={resTotalResources}
        onChange={handleChange}
        disabled={true}
      />

      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (B - A) Surplus / Shortfall
        </Typography>
      </Grid>
      <GridTextfield
        id="criSurplusShortfall"
        key="criSurplusShortfall"
        columnSize="3"
        isCurrency={true}
        currState={criSurplusShortfall}
        onChange={handleChange}
        disabled={true}
      />
      {/*==============================
        REASON FOR DEVIATION
      ==============================*/}
      <Grid size={12}>
        <Typography variant="body1">
          <b>Reason(s) for Deviations:</b>
        </Typography>

        <TextareaAutosize
          id="deviationReason"
          placeholder="Type here..."
          style={{ width: "100%" }}
          minRows={5}
          onChange={handleChange}
          defaultValue={deviationReason}
        />
        {deviationReasonError && <FormHelperText error={deviationReasonError}>{deviationReasonError}</FormHelperText>}
      </Grid>
    </>
  );
}
